import type { AlertColor } from '@mui/material';

/**
 * The `type` values the flag accepts, mapped onto MUI severities. The flag's
 * vocabulary is deliberately not MUI's — `warn`/`danger` read better to whoever
 * is typing the value into LaunchDarkly than `warning`/`error` do.
 */
const SEVERITY_BY_TYPE: Record<string, AlertColor> = {
  info: 'info',
  warn: 'warning',
  danger: 'error',
};

/**
 * `inline` sits inside the card, just below the logo. `top` is full-bleed across
 * the top of the page. Both show on every screen rendered through
 * `AuthenticationPage` — sign-in, account picker, org selection and the password
 * screens alike — since a notice that hides behind a screen change is easy to miss.
 */
export type BannerMessagePlacement = 'inline' | 'top';

export type BannerMessage = {
  severity: AlertColor;
  text: string;
  placement: BannerMessagePlacement;
};

/**
 * Parses the `bannerMessage` LaunchDarkly flag, a JSON document shaped
 * `{ "enabled": true, "type": "warn", "placement": "top", "message": "..." }`.
 * The signed-in app's `inAppBanner` flag shares the shape (minus `placement`).
 *
 * `message` may be HTML. It is returned as-is in `text` and sanitized where it
 * is rendered, by `HtmlMessage`.
 *
 * Must be a **JSON** flag in LaunchDarkly. A String flag renders nothing, since
 * there is no sane reading of a bare string that does not re-introduce the
 * prefix-parsing guesswork this shape exists to remove.
 *
 * `bannerMessage.schema.json` documents the shape for whoever authors the value.
 * It is deliberately stricter than this function: the schema describes a
 * well-formed document (every field present, no unknown keys), while this reads
 * whatever actually arrives. LaunchDarkly does not enforce schemas on flag
 * values, so nothing guarantees the two agree at runtime — hence the tolerance.
 *
 * Returns null whenever there is nothing to show: `enabled` not exactly `true`,
 * an empty or missing `message`, `{}`, `null`, or the moment before the SDK has
 * any flags at all (`undefined`).
 *
 * `enabled` must be *explicitly* true — an absent or non-boolean value hides the
 * banner. This field exists to gate visibility, so ambiguity resolves to hidden:
 * a half-edited document staying quiet costs the operator one puzzled look at the
 * login page, whereas defaulting it visible shows draft text to every user.
 *
 * `type` and `placement` take the opposite default, because they are cosmetic
 * rather than gates. Anything unrecognised falls back to `info` and `inline` —
 * the quieter of the two placements — so a typo downgrades the presentation
 * instead of swallowing the text or shouting it across the page.
 */
const parseBannerMessage = (value: unknown): BannerMessage | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;

  const { enabled, type, placement, message } = value as {
    enabled?: unknown;
    type?: unknown;
    placement?: unknown;
    message?: unknown;
  };

  if (enabled !== true) return null;
  if (typeof message !== 'string') return null;

  const text = message.trim();
  if (!text) return null;

  const severity =
    typeof type === 'string' ? SEVERITY_BY_TYPE[type.trim().toLowerCase()] : undefined;

  // Only `top` opts out of the default, so every other value lands on `inline`.
  const isTop = typeof placement === 'string' && placement.trim().toLowerCase() === 'top';

  return { severity: severity ?? 'info', text, placement: isTop ? 'top' : 'inline' };
};

export default parseBannerMessage;
